import React from 'react';
import { Settings, Plus, Pencil, Trash2, X, Save } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

const PresetManagementModal = ({
  showModal,
  onClose,
  presets,
  editingPreset,
  presetForm,
  setPresetForm,
  presetSaving,
  presetError,
  onStartNewPreset,
  onStartEditPreset,
  onCancelEdit,
  onSavePreset,
  onDeletePreset
}) => {
  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Manage Presets
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Add New Preset Button */}
          {!editingPreset && !presetForm.label && (
            <button
              onClick={onStartNewPreset}
              className="w-full mb-4 p-3 border-2 border-dashed border-teal-300 rounded-lg text-teal-600 hover:bg-teal-50 hover:border-teal-400 transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <Plus className="w-5 h-5" />
              Save Current Values as New Preset
            </button>
          )}

          {/* Edit/Add Form */}
          {(editingPreset || presetForm.base) && (
            <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-medium text-slate-700 mb-3">
                {editingPreset ? 'Edit Preset' : 'New Preset'}
              </h3>
              
              {presetError && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                  {presetError}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-600">Preset Name *</Label>
                  <Input
                    value={presetForm.label}
                    onChange={(e) => setPresetForm({ ...presetForm, label: e.target.value })}
                    placeholder="Enter preset name..."
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Base</Label>
                    <Input
                      type="number"
                      value={presetForm.base}
                      onChange={(e) => setPresetForm({ ...presetForm, base: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Left Side</Label>
                    <Input
                      type="number"
                      value={presetForm.leftSide}
                      onChange={(e) => setPresetForm({ ...presetForm, leftSide: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Right Side</Label>
                    <Input
                      type="number"
                      value={presetForm.rightSide}
                      onChange={(e) => setPresetForm({ ...presetForm, rightSide: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Left Lip</Label>
                    <Input
                      type="number"
                      value={presetForm.leftLip}
                      onChange={(e) => setPresetForm({ ...presetForm, leftLip: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Right Lip</Label>
                    <Input
                      type="number"
                      value={presetForm.rightLip}
                      onChange={(e) => setPresetForm({ ...presetForm, rightLip: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Thickness</Label>
                    <Input
                      type="number"
                      value={presetForm.thickness}
                      onChange={(e) => setPresetForm({ ...presetForm, thickness: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={onSavePreset}
                    disabled={presetSaving}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {presetSaving ? 'Saving...' : (editingPreset ? 'Update Preset' : 'Save Preset')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onCancelEdit}
                    disabled={presetSaving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Presets List */}
          <div className="space-y-2">
            <h3 className="font-medium text-slate-700 text-sm uppercase tracking-wide">
              All Presets ({presets.length})
            </h3>
            {presets.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">No presets available</p>
            ) : (
              <div className="space-y-1">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">{preset.label}</div>
                      <div className="text-xs text-slate-500">
                        Base: {preset.base} | Sides: {preset.leftSide}/{preset.rightSide} | Lips: {preset.leftLip}/{preset.rightLip} | T: {preset.thickness}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => onStartEditPreset(preset)}
                        className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit preset"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeletePreset(preset.id)}
                        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete preset"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PresetManagementModal;
